import { useRef, useState } from 'react'
import { InboxOutlined } from '@ant-design/icons'
import { Form, Image, Upload, UploadFile } from 'antd'
import classNames from 'classnames'
import { Rule } from 'antd/es/form'
import removePhoto from 'assets/icons/remove-photo.svg'
import { showMessage } from 'shared/utils/showMessage'
import { normalizeFile } from 'shared/utils/normalizeFile'
import { getImageSize } from 'shared/utils/getImageSize'
import styles from './Uploader.module.scss'

interface IUploaderProps {
  name: string
  rules: Rule[]
  disabled?: boolean
  // только просмотр: загруженное изображение нельзя убрать
  readOnly?: boolean
  multiple?: boolean
  maxCount?: number
  type?: 'default' | 'variation'
  onRemoveClick?: (file: UploadFile) => void
  hint?: React.ReactNode
  minSize?: number
  sortable?: boolean
}

export const Uploader: React.FC<IUploaderProps> = ({
  name,
  rules,
  disabled = false,
  readOnly = false,
  multiple = false,
  maxCount = 1,
  type = 'default',
  onRemoveClick = () => false,
  hint,
  minSize,
  sortable = false
}) => {
  const isTypeDefault = type === 'default'
  const form = Form.useFormInstance()
  const fileList = Form.useWatch<UploadFile[] | undefined>(name, form)
  const dragIndex = useRef<number | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)
  // достигнут лимит фото — прячем зону загрузки, оставляя список (превью)
  const isFull = (fileList?.length ?? 0) >= maxCount

  const movePhoto = (from: number, to: number) => {
    const list: UploadFile[] = form.getFieldValue(name) ?? []
    if (from === to || !list[from]) return
    const next = [...list]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    form.setFieldValue(name, next)
  }

  const validateSize = async (file: File) => {
    if (!minSize) return true
    const size = await getImageSize(file)
    if (!size) {
      showMessage('error', 'Не удалось прочитать изображение', file.name)
      return false
    }
    if (Math.min(size.width, size.height) >= minSize) return true
    showMessage(
      'error',
      `Недостаточное разрешение изображения — ${size.width}x${size.height} px`,
      file.name
    )
    return false
  }

  return (
    <Form.Item
      name={name}
      valuePropName='fileList'
      getValueFromEvent={normalizeFile}
      rules={rules}
      className={classNames({ [styles.full]: isFull })}
      style={{ marginBottom: isTypeDefault ? 24 : 10 }}
      hasFeedback
    >
      <Upload.Dragger
        multiple={multiple}
        maxCount={maxCount}
        disabled={disabled}
        accept='image/png, image/jpeg, image/jpg, image/webp'
        listType='picture'
        beforeUpload={async (file) => {
          const isValid =
            file.type === 'image/png' ||
            file.type === 'image/jpg' ||
            file.type === 'image/jpeg' ||
            file.type === 'image/webp'
          if (!isValid) {
            showMessage(
              'error',
              'Поддерживаются только форматы JPG, JPEG, PNG, WebP',
              ''
            )
            return Upload.LIST_IGNORE
          }
          return (await validateSize(file)) || Upload.LIST_IGNORE
        }}
        // Files are sent with the parent form. Finish the local selection so Upload
        // does not display an endless network spinner before the form is submitted.
        customRequest={({ onSuccess }) => onSuccess?.('ok')}
        itemRender={(_, file, fileList, { remove }) => {
          const index = fileList.indexOf(file)
          const isDragged = sortable && dragIndex.current === index
          return (
            <div
              data-testid={`uploader-photo-${index}`}
              className={classNames(styles.photoWrapper, {
                [styles.sortable]: sortable,
                [styles.dragged]: isDragged,
                [styles.dropTarget]: dropIndex === index && !isDragged
              })}
              draggable={sortable}
              // без setData браузер не начинает перетаскивание
              onDragStart={(e) => {
                dragIndex.current = index
                e.dataTransfer.effectAllowed = 'move'
                e.dataTransfer.setData('text/plain', String(index))
              }}
              onDragEnd={() => {
                dragIndex.current = null
                setDropIndex(null)
              }}
              // без stopPropagation перетаскивание превью ловит зона загрузки файлов
              onDragOver={(e) => {
                if (dragIndex.current === null) return
                e.preventDefault()
                e.stopPropagation()
                e.dataTransfer.dropEffect = 'move'
                setDropIndex(index)
              }}
              onDragLeave={() =>
                setDropIndex((prev) => (prev === index ? null : prev))
              }
              onDrop={(e) => {
                if (dragIndex.current === null) return
                e.preventDefault()
                e.stopPropagation()
                movePhoto(dragIndex.current, index)
                dragIndex.current = null
                setDropIndex(null)
              }}
            >
              <Image
                // новые файлы — base64 thumbUrl; уже сохранённые (режим редактирования) — url
                src={file.thumbUrl || file.url}
                preview={!file.originFileObj}
                alt='Изображение'
                width={isTypeDefault ? 154 : undefined}
                height={isTypeDefault ? 150 : undefined}
                className={isTypeDefault ? styles.photo : undefined}
                style={{ maxHeight: isTypeDefault ? undefined : 280 }}
              />
              {sortable && index === 0 && (
                <span className={styles.mainBadge}>Главное фото</span>
              )}
              {!readOnly && (
                <button
                  type='button'
                  className={classNames('reset-button', styles.removePhoto)}
                  onClick={() => {
                    onRemoveClick(file)
                    remove()
                  }}
                >
                  <img src={removePhoto} alt='Удалить изображение' />
                </button>
              )}
            </div>
          )
        }}
      >
        <div className={styles.descriptionWrapper}>
          <p className='ant-upload-drag-icon'>
            <InboxOutlined />
          </p>
          <p className='ant-upload-text'>
            Нажмите или перетащите файл в эту область, чтобы загрузить
          </p>
          <p className='ant-upload-hint'>
            {`Поддерживается одиночная ${
              multiple ? 'и массовая ' : ''
            }загрузка.`}
          </p>
          {hint && (
            <p className={classNames('ant-upload-hint', styles.hint)}>{hint}</p>
          )}
        </div>
      </Upload.Dragger>
    </Form.Item>
  )
}
